import React, { FC } from "react";
import type { PdpUrlVideoLink } from "../../../../../components/PdpPages/PdpJsonLdOutput";
import { getVideoThumbnail } from "../../../../../utils/downloadHelpers";
import { formatVideoName } from "../../../videos/hooks/useFetchVideos";
import { pdpVideoHref } from "../../../videos/selectedVideoCardV2/pdpAssignment";
import { IconFilmGlyph } from "../../../videos/selectedVideoCardV2/panels";
import { btnClass } from "../components/ui";
import { notOnPageNote, ourVideoMeta } from "./pageModel";
import type { PdpUrl } from "./pageModel";

/**
 * "Our videos on this page N" (handoff README:182) — the modal's linked-video
 * cards (MODAL/ProductPdpUrlsPanel.tsx:588-652) in the handoff's row form.
 *
 * Nothing the modal shows is dropped: the name (formatVideoName, :592), the
 * video file URL the retailer serves (:636-641), and the not-on-page note
 * (:642-644). The handoff's "0:48 · found in a video tag" line prints ONLY
 * when the last analysis matched a page video to this link
 * (`linkForPageVideo`, PdpJsonLdOutput.tsx:87) — the plan's "Our videos" row.
 * View video deep-links as the modal does (`pdpVideoHref`, :483); Remove from
 * page asks first — the section owns that confirmation (:882-891).
 *
 * The removed video's button leaves with it, so the section puts focus on the
 * next video's Remove from page (else the one before), or on this row's
 * heading when none is left — never on <body>. Hence the ids below.
 */

/** The row heading: the section's focus target once the page has no video left. */
export const videosHeadingId = (blockId: string) => `${blockId}-videos`;
/** A video's Remove from page button. */
export const removeVideoId = (blockId: string, s3FileId: number) => `${blockId}-remove-${s3FileId}`;

interface Props {
  row: PdpUrl;
  /** The block's article id — the ids above derive from it. */
  blockId: string;
  /** `isInternal(role)` — picks the admin or the brand videos page. */
  internal: boolean;
  onRemove: (link: PdpUrlVideoLink) => void;
}

export const OurVideosRow: FC<Props> = ({ row, blockId, internal, onRemove }) => {
  const links = row.videos || [];
  return (
    <div className="flex flex-col gap-2.5 py-3 border-t border-border-token">
      <div
        id={videosHeadingId(blockId)}
        tabIndex={-1}
        className="flex items-center gap-2 self-start text-text-1"
        style={{ fontSize: 13 }}
      >
        Our videos on this page
        <span className="text-text-2 tabular-nums">{links.length}</span>
      </div>
      {links.length === 0 ? (
        <span className="text-text-2" style={{ fontSize: 12.5 }}>
          None of our videos is on this page yet.
        </span>
      ) : (
        <ul className="m-0 p-0 list-none flex flex-col gap-3">
          {links.map((link) => {
            const name = formatVideoName(link.videoFile?.name || link.videoFile?.key) || "Linked video";
            const href = pdpVideoHref(internal, link.s3_file_id);
            const meta = ourVideoMeta(row, link);
            const note = notOnPageNote(row, link);
            return (
              <li key={link.id} className="flex flex-wrap items-center gap-x-3.5 gap-y-2 min-w-0">
                <img
                  src={getVideoThumbnail(link.videoFile || {})}
                  alt=""
                  className="block shrink-0 rounded-md object-cover bg-surface-2"
                  style={{ width: 72, height: 40 }}
                />
                {/* 160px: beside the 72px thumbnail within a 375px page block (317px inside). */}
                <div className="flex flex-col gap-0.5 min-w-0" style={{ flex: "1 1 160px" }}>
                  <span className="text-text-1 truncate" style={{ fontSize: 13 }} title={name}>
                    {name}
                  </span>
                  {meta && (
                    <span className="text-text-2" style={{ fontSize: 11.5 }}>
                      {meta}
                    </span>
                  )}
                  {link.video_file_url && (
                    <span className="flex items-center gap-1.5 min-w-0 text-text-2" style={{ fontSize: 11.5 }}>
                      <span className="inline-flex shrink-0" aria-hidden="true">
                        <IconFilmGlyph size={13} strokeWidth={1.8} />
                      </span>
                      <span className="truncate" title={link.video_file_url}>
                        {link.video_file_url}
                      </span>
                    </span>
                  )}
                  {note && (
                    <span className="text-down" style={{ fontSize: 11.5 }}>
                      {note}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3.5">
                  {/* Each names its video: every row's pair would read the same otherwise. */}
                  {href && (
                    <a
                      href={href}
                      target="_blank"
                      rel="noreferrer"
                      className={btnClass("text")}
                      aria-label={`View video, ${name} (opens in a new tab)`}
                    >
                      View video
                    </a>
                  )}
                  <button
                    id={removeVideoId(blockId, link.s3_file_id)}
                    type="button"
                    onClick={() => onRemove(link)}
                    className={btnClass("text", { danger: true })}
                    aria-label={`Remove from page, ${name}`}
                    title="Remove this video from the page — the page, its SKUs, its score and any other videos on it stay"
                  >
                    Remove from page
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

